export const AMENITY_FORM_YES = 'yes';
export const AMENITY_FORM_NO = 'no';
export const AMENITY_FORM_UNKNOWN = 'unknown';

/**
 * @param {boolean | undefined} storedValue
 * @returns {'yes' | 'no' | 'unknown'}
 */
export function storedAmenityToFormValue(storedValue) {
  if (storedValue === true) {
    return AMENITY_FORM_YES;
  }
  if (storedValue === false) {
    return AMENITY_FORM_NO;
  }
  return AMENITY_FORM_UNKNOWN;
}

/**
 * @param {Record<string, boolean> | null | undefined} storedAmenities
 * @param {string[]} amenityKeys
 * @returns {Record<string, 'yes' | 'no' | 'unknown'>}
 */
export function normalizeAmenityFormValues(storedAmenities, amenityKeys) {
  const normalized = {};
  const source = storedAmenities ?? {};

  amenityKeys.forEach((amenityKey) => {
    normalized[amenityKey] = storedAmenityToFormValue(source[amenityKey]);
  });

  return normalized;
}

/**
 * @param {Record<string, 'yes' | 'no' | 'unknown'> | null | undefined} formAmenities
 * @param {string[]} amenityKeys
 * @returns {Record<string, boolean>}
 */
export function serializeAmenityOverrides(formAmenities, amenityKeys) {
  const serialized = {};
  const source = formAmenities ?? {};

  amenityKeys.forEach((amenityKey) => {
    const formValue = source[amenityKey];
    if (formValue === AMENITY_FORM_YES) {
      serialized[amenityKey] = true;
    } else if (formValue === AMENITY_FORM_NO) {
      serialized[amenityKey] = false;
    }
  });

  return serialized;
}

/**
 * @param {Record<string, 'yes' | 'no' | 'unknown'> | null | undefined} formAmenities
 * @param {Record<string, boolean> | null | undefined} existingOverrideAmenities
 * @param {string[]} amenityKeys
 * @param {Function} deleteFieldFn
 * @returns {Record<string, unknown> | null}
 */
export function buildAmenityDeleteFields(
  formAmenities,
  existingOverrideAmenities,
  amenityKeys,
  deleteFieldFn,
) {
  const existing = existingOverrideAmenities ?? {};
  const source = formAmenities ?? {};
  const nestedDeletes = {};

  amenityKeys.forEach((amenityKey) => {
    const formValue = source[amenityKey] ?? AMENITY_FORM_UNKNOWN;
    const hadExistingOverride = Object.prototype.hasOwnProperty.call(existing, amenityKey);
    if (formValue === AMENITY_FORM_UNKNOWN && hadExistingOverride) {
      nestedDeletes[amenityKey] = deleteFieldFn();
    }
  });

  if (Object.keys(nestedDeletes).length === 0) {
    return null;
  }

  return nestedDeletes;
}

export const AMENITY_UNKNOWN_SELECTION_HINT = 'Defaults to underlying profile value';

/**
 * Display hint for override choices after save (unknown returns empty string).
 *
 * @param {'yes' | 'no' | 'unknown'} formValue
 * @param {{ label: string, antiLabel: string, displayAnti?: boolean }} amenityType
 * @returns {string}
 */
export function getAmenitySaveHint(formValue, amenityType) {
  if (formValue === AMENITY_FORM_YES) {
    return amenityType.label;
  }
  if (formValue === AMENITY_FORM_NO) {
    return amenityType.displayAnti ? amenityType.antiLabel : 'Not shown';
  }
  return '';
}

/**
 * @param {boolean} updateAmenities
 * @param {Record<string, boolean> | null | undefined} overrideAmenities
 * @param {string[]} amenityKeys
 * @returns {Record<string, 'yes' | 'no' | 'unknown'>}
 */
export function buildInitialAmenityFormValues(updateAmenities, overrideAmenities, amenityKeys) {
  if (!updateAmenities) {
    return {};
  }
  return normalizeAmenityFormValues(overrideAmenities, amenityKeys);
}
